<script setup>
import dayjs from 'dayjs'
import { isEmpty, isEqual, isNull, uniq } from 'lodash'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  DATE_UNITS,
  ELEMENT_PERIODS_DATE_FORMAT,
  getMonthEndDateFromObject,
  getMothObjectFromDate,
  UNSELECTED_DATE
} from '@/utils/date'

import AppVueDatePicker from '@/components/form/AppVueDatePicker'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDivider from '@/components/ui/AppDivider'
import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'
import {
  CHECKIN_FEED_GO_TO_LATEST_BUTTON_TEST_ID,
  CHECKIN_FEED_NAVIGATION_BUTTON_TEST_ID
} from '@/views/check-ins/jest-helpers'

defineOptions({
  name: 'CheckinsFeedNavigation'
})

const props = defineProps({
  allowedDates: {
    type: Array,
    default: () => []
  },

  overrideDate: {
    type: [String, Date, Number],
    default: UNSELECTED_DATE
  },

  disabled: {
    type: Boolean
  },

  hasGoToLatestAction: {
    type: Boolean
  },

  loading: {
    type: Boolean
  }
})

const model = defineModel({
  required: true,
  validator: v => isNull(v) || dayjs(v).isValid(),
  set: v => v
})

const emit = defineEmits({
  'update:model-value': null,
  'go-to-latest': null
})

const selectedDate = ref(UNSELECTED_DATE)

const onSelectMonth = date => {
  if (props.disabled) return

  if (selectedDateIndex.value === normalizedAllowedDates.value.length - 1) {
    model.value = null
    emit('update:model-value', null)
  } else {
    model.value = getMonthEndDateFromObject(date)
  }
}

const normalizeDateForCompare = d => dayjs(d).format(ELEMENT_PERIODS_DATE_FORMAT)

const normalizedAllowedDates = computed(() => uniq(props.allowedDates.map(normalizeDateForCompare)))

const edgeDates = computed(() => {
  if (isEmpty(normalizedAllowedDates.value)) return { mostDistantDate: null, mostRecentDate: null }

  return {
    mostDistantDate: normalizedAllowedDates.value[0],
    mostRecentDate: normalizedAllowedDates.value.at(-1)
  }
})

const isDateDisabled = date => {
  return !normalizedAllowedDates.value.includes(normalizeDateForCompare(date))
}

watch(
  () => edgeDates.value.mostRecentDate,
  value => {
    if (value) {
      selectedDate.value = getMothObjectFromDate(value)
    }
  },
  { immediate: true }
)

const selectMostRecentDate = () => {
  if (edgeDates.value.mostRecentDate) {
    selectedDate.value = getMothObjectFromDate(edgeDates.value.mostRecentDate)
  } else {
    selectedDate.value = UNSELECTED_DATE
  }
}

watch(
  () => model.value,
  value => {
    if (isNull(value)) {
      selectMostRecentDate()
    }
  }
)

watch(
  () => props.overrideDate,
  value => {
    if (value && dayjs(value).isValid()) {
      const isDateAllowed = normalizedAllowedDates.value.includes(
        normalizeDateForCompare(dayjs(value).startOf(DATE_UNITS.MONTH))
      )
      if (isDateAllowed) {
        selectedDate.value = getMothObjectFromDate(value)
      }
    }
  }
)

const selectedDateIndex = computed(() => {
  if (!selectedDate.value) return -1
  return normalizedAllowedDates.value.findIndex(d =>
    isEqual(getMothObjectFromDate(d), selectedDate.value)
  )
})

const moveTo = (step = 0) => {
  if (props.disabled) return
  const newIndex = selectedDateIndex.value + step
  if (newIndex < 0 || newIndex >= normalizedAllowedDates.value.length) return
  selectedDate.value = getMothObjectFromDate(normalizedAllowedDates.value[newIndex])
  onSelectMonth(selectedDate.value)
}

const BACKWARD = 'backward'
const FORWARD = 'forward'

const isEdgeDate = (direction = BACKWARD) => {
  if (!selectedDate.value) return true

  const targetDate =
    direction === BACKWARD ? edgeDates.value.mostDistantDate : edgeDates.value.mostRecentDate

  return targetDate && isEqual(getMothObjectFromDate(targetDate), selectedDate.value)
}

const isBackwardDisabled = computed(() => isEdgeDate(BACKWARD))

const isForwardDisabled = computed(() => isEdgeDate(FORWARD))

const DATEPICKER = 'datepicker'
const LATEST = 'latest'

const { t } = useI18n()

const navigationButtons = computed(() => {
  return [
    {
      name: LATEST,
      text: t('checkin.latest'),
      handler: () => emit('go-to-latest'),
      shouldBeHidden: !props.hasGoToLatestAction,
      hasDivider: true,
      testId: CHECKIN_FEED_GO_TO_LATEST_BUTTON_TEST_ID
    },
    {
      name: BACKWARD,
      icon: 'chevron-left-next',
      handler: () => moveTo(-1),
      disabled: isBackwardDisabled.value,
      testId: CHECKIN_FEED_NAVIGATION_BUTTON_TEST_ID
    },

    {
      name: DATEPICKER
    },

    {
      name: FORWARD,
      icon: 'chevron-right-next',
      handler: () => moveTo(1),
      disabled: isForwardDisabled.value,
      testId: CHECKIN_FEED_NAVIGATION_BUTTON_TEST_ID
    }
  ].filter(action => !action.shouldBeHidden)
})
</script>

<template>
  <div class="cfn-Wrapper">
    <template v-for="action in navigationButtons" :key="action.name">
      <AppVueDatePicker
        v-if="action.name === DATEPICKER"
        v-model="selectedDate"
        :disabled-dates="isDateDisabled"
        :offset="8"
        :read-only="disabled"
        class="cfn-DatePicker"
        month-picker
        position="right"
        @update:model-value="onSelectMonth($event)"
      >
        <template #button="{ content }">
          <AppButton
            :class="{
              'cfn-Button-disabled': disabled
            }"
            :disable="disabled"
            class="cfn-Button"
            type="tertiary-next"
          >
            <SkeletonItem v-if="loading" class="cfn-Button_Loader" height="100%" width="100%" />

            {{ content }}
          </AppButton>
        </template>
      </AppVueDatePicker>

      <template v-else>
        <AppButton
          :class="{
            'cfn-Button-disabled': action.disabled || disabled
          }"
          :data-auto-testid="action.testId"
          :data-testid="action.testId"
          :disable="action.disabled || disabled || isEmpty(normalizedAllowedDates)"
          :icon="action.icon"
          :remove-padding="!action.text"
          class="cfn-Button"
          type="tertiary-next"
          @click="action.handler"
        >
          <template v-if="action.text">
            {{ action.text }}
          </template>
        </AppButton>

        <AppDivider v-if="action.hasDivider" vertical />
      </template>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';

.cfn-Wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cfn-Button {
  min-width: 32px;
  background-color: $grey-3-next;
  font-weight: fw('bold');
  font-size: $fs-12;
  line-height: 16px;
  position: relative;

  &:not(&-disabled) {
    @include hoverState($grey-2-next, 1%);
    @include activeState($grey-3-next, 5%);
  }
}

.cfn-Button_Loader {
  position: absolute;
  top: 0;
  left: 0;
}

.cfn-DatePicker {
  width: auto;
}
</style>
